import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { Theme, useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { modalBuyTokensStyles } from './modalBuyTokensStyles';
import { CardModalBuyTokens } from '../CardModalBuyTokens/CardModalBuyTokens';
import { urlLang } from '../../helpers/langHelper';
import {
  API_KEY_ONRAMPER,
  ASSETS_PATH,
  UNIT_ONRAMPER
} from '../../services/endpoints';

export type LinksListItem = {
  name: string;
  link: string;
  image: string;
  subtitle: string;
  chipText?: string;
  usPerson?: boolean;
};

export interface IModalBuyTokensProps {
  open: boolean;
  onClose: () => void;
  onContinue?: () => void;
  isLoading?: boolean;
}

export const ModalBuyTokens = ({
  open,
  onClose,
  isLoading
}: IModalBuyTokensProps) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const lang = urlLang();
  const isDark: boolean = theme.palette.mode === 'dark';
  const classes = modalBuyTokensStyles(theme, isDark);
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const typographyVariant = isDownMd ? 'h5' : 'h4';
  const onRamperBnbPath = `https://widget.onramper.com/?apiKey=${API_KEY_ONRAMPER}&defaultCrypto=${UNIT_ONRAMPER}&language=${lang}`;

  const linksList: LinksListItem[] = [
    {
      name: t('vesting.modal_buy_tokens.list.onramper.name'),
      link: onRamperBnbPath,
      image: `${ASSETS_PATH}/img/onramper_logo.png`,
      subtitle: t('vesting.modal_buy_tokens.list.onramper.subtitle'),
      chipText: t('vesting.modal_buy_tokens.list.onramper.chip')
    },
    {
      name: t('vesting.modal_buy_tokens.list.mexc.name'),
      link: t('vesting.modal_buy_tokens.list.mexc.link'),
      image: `${ASSETS_PATH}/img/mexc_logo.png`,
      subtitle: t('vesting.modal_buy_tokens.list.mexc.subtitle'),
      chipText: t('vesting.modal_buy_tokens.list.mexc.chip')
    },
    {
      name: t('vesting.modal_buy_tokens.list.bitmart.name'),
      link: t('vesting.modal_buy_tokens.list.bitmart.link'),
      image: `${ASSETS_PATH}/img/bitmart_logo.png`,
      subtitle: t('vesting.modal_buy_tokens.list.bitmart.subtitle'),
      chipText: t('vesting.modal_buy_tokens.list.bitmart.chip'),
      usPerson: true
    },
    {
      name: t('vesting.modal_buy_tokens.list.portal.name'),
      link: t('vesting.modal_buy_tokens.list.portal.link'),
      image: `${ASSETS_PATH}/img/portal_logo.png`,
      subtitle: t('vesting.modal_buy_tokens.list.portal.subtitle')
    }
  ];

  return (
    <Paper sx={classes.containerPaper}>
      <Dialog
        fullScreen={isDownSm}
        onClose={onClose}
        open={open}
        sx={classes.container}
        disableScrollLock={true}
      >
        <Box sx={classes.titleSection}>
          <DialogTitle sx={classes.dialogTitle} variant={typographyVariant}>
            {t('vesting.modal_onramper.dialog.title')}
          </DialogTitle>
          <IconButton
            onClick={onClose}
            sx={classes.closeIconButton}
            disabled={isLoading}
          >
            <CloseIcon sx={classes.closeIconSvg} />
          </IconButton>
        </Box>
        <DialogContent sx={classes.modalContainer}>
          <Grid
            container
            item
            direction={{ xs: 'column', sm: 'row' }}
            columnSpacing={3}
          >
            {linksList.map((item: LinksListItem, itemIndex: number) => (
              <Grid
                item
                key={`linksListItemGrid${itemIndex}`}
                xs={12}
                sm={6}
                md={6}
                sx={classes.containerCard}
              >
                <CardModalBuyTokens
                  text={item?.name}
                  href={item?.link}
                  image={item?.image}
                  subtitle={item?.subtitle}
                  chipText={item?.chipText}
                  usPerson={item?.usPerson}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
